// cspell:ignore hitbox hitboxes
import type { LineMetric } from '@meitu/x-design-text';
import type { TextRenderSnapshot as Snapshot } from '@/framework/services/text-render';
import type { TextSelectionRange } from '@/utils/text-kit';
import { TextAlignType, type IPoint, type Rect } from '@/version-types';
import {
    buildHitboxes,
    findHitboxByInline,
    findLineIndexByPoint,
    getHitboxesForLine,
    getHitboxesForSnapshotLine,
    getHitboxInlineSize,
    getHitboxInlineStart,
    getVisualEdgeOffset,
    groupByVisualRun,
    type HitBox,
} from './hitbox';

/**
 * 查找字节偏移量所在的行。
 *
 * @param lineMetrics - 排版行信息。
 * @param offset - UTF-8 字节偏移量。
 * @returns 匹配的行索引；越界时返回首行或末行索引。
 */
const findLineIndexByOffset = (lineMetrics: readonly LineMetric[], offset: number) => {
    const index = lineMetrics.findIndex((line) => offset >= line.byteStart && offset <= line.byteEnd);
    if (index >= 0) return index;
    return offset > (lineMetrics[lineMetrics.length - 1]?.byteEnd ?? 0) ? lineMetrics.length - 1 : 0;
};

/**
 * 读取坐标点在当前书写方向上的行内坐标。
 *
 * @param snapshot - 当前排版快照。
 * @param point - 画布坐标点。
 * @returns 横排时返回 x，竖排时返回 y。
 */
const getPointInline = (snapshot: Snapshot, point: IPoint) =>
    snapshot.writingMode === 'vertical-rl' ? point.y : point.x;

/**
 * 读取排版行在当前书写方向上的起点。
 *
 * @param snapshot - 当前排版快照。
 * @param line - 目标排版行。
 * @returns 包含列表缩进的行内起点。
 */
const getLineInlineStart = (snapshot: Snapshot, line: LineMetric) =>
    (snapshot.writingMode === 'vertical-rl' ? line.top : line.leftEdgeX) + snapshot.listMarkerIndent;

/**
 * 读取排版行在当前书写方向上的终点。
 *
 * @param snapshot - 当前排版快照。
 * @param line - 目标排版行。
 * @returns 包含列表缩进的行内终点。
 */
const getLineInlineEnd = (snapshot: Snapshot, line: LineMetric) =>
    (snapshot.writingMode === 'vertical-rl' ? line.bottom : line.rightEdgeX) + snapshot.listMarkerIndent;

/**
 * 根据命中盒中点和双向文本方向解析光标字节偏移。
 *
 * @param hitbox - 命中的字形盒。
 * @param inline - 当前书写方向上的行内坐标。
 * @returns 命中盒视觉前侧或后侧对应的字节偏移。
 */
const resolveHitboxOffset = (hitbox: HitBox, inline: number) => {
    const visuallyBefore =
        getHitboxInlineSize(hitbox) === 0
            ? true
            : inline < getHitboxInlineStart(hitbox) + getHitboxInlineSize(hitbox) / 2;
    const reverse = hitbox.bidiLevel % 2 === 1;
    const affinity = reverse ? (visuallyBefore ? 'after' : 'before') : visuallyBefore ? 'before' : 'after';
    return affinity === 'before' ? hitbox.byteStart : hitbox.byteEnd;
};

/**
 * 将画布坐标转换为折叠文本选区。
 *
 * 先通过 line metrics 定位目标行，再只按需构建该行的 hitboxes；点击定位无需扫描和创建全文命中盒。
 * 行内命中继续保留 bidi 视觉边缘与 UTF-8 字节偏移语义。
 *
 * @param snapshot - 当前排版快照。
 * @param point - 画布坐标点。
 * @returns 坐标命中的 UTF-8 字节选区。
 */
export const getTextRangeFromPoint = (snapshot: Snapshot | null, point: IPoint): TextSelectionRange => {
    if (!snapshot || snapshot.lineMetrics.length === 0) return { start: 0, end: 0 };

    const lineIndex = findLineIndexByPoint(snapshot, point);
    const line = snapshot.lineMetrics[lineIndex];
    if (!line) return { start: 0, end: 0 };

    const hitboxes = getHitboxesForSnapshotLine(snapshot, lineIndex);
    const inline = getPointInline(snapshot, point);
    const hitbox = findHitboxByInline(hitboxes, inline);
    const offset = hitbox
        ? resolveHitboxOffset(hitbox, inline)
        : hitboxes[0]
          ? getVisualEdgeOffset(hitboxes[0], 'start')
          : line.byteStart;
    return { start: offset, end: offset };
};

/**
 * 将指定字节偏移转换为目标行上的行内坐标。
 *
 * @param snapshot - 当前排版快照。
 * @param hitboxes - 目标行中按视觉顺序排列的命中盒。
 * @param line - 目标排版行。
 * @param lineIndex - 目标行索引。
 * @param offset - UTF-8 字节偏移量。
 * @returns 当前书写方向上的行内坐标。
 */
const getOffsetInline = (
    snapshot: Snapshot,
    hitboxes: readonly HitBox[],
    line: LineMetric,
    lineIndex: number,
    offset: number,
) => {
    const lineHitboxes = getHitboxesForLine(hitboxes, lineIndex);
    const atStart = lineHitboxes.find((hitbox) => hitbox.byteStart === offset);
    if (atStart)
        return atStart.bidiLevel % 2 === 1
            ? getHitboxInlineStart(atStart) + getHitboxInlineSize(atStart)
            : getHitboxInlineStart(atStart);

    const atEnd = lineHitboxes.find((hitbox) => hitbox.byteEnd === offset);
    if (atEnd)
        return atEnd.bidiLevel % 2 === 1
            ? getHitboxInlineStart(atEnd)
            : getHitboxInlineStart(atEnd) + getHitboxInlineSize(atEnd);

    const containing = lineHitboxes.find((hitbox) => hitbox.byteStart < offset && offset < hitbox.byteEnd);
    if (containing)
        return containing.bidiLevel % 2 === 1
            ? getHitboxInlineStart(containing) + getHitboxInlineSize(containing)
            : getHitboxInlineStart(containing);

    if (offset >= line.byteEnd) return getLineInlineEnd(snapshot, line);
    return getLineInlineStart(snapshot, line);
};

/**
 * 计算折叠选区的光标矩形。
 *
 * 折叠 caret 只依赖其所在行，因此只读取该行的懒缓存 hitboxes；非折叠选区直接返回 null，仍由
 * 选区几何路径按需消费多行数据。
 *
 * @param snapshot - 当前排版快照。
 * @param range - 当前文本选区。
 * @param fallbackHeight - 无快照时使用的光标高度。
 * @param fallbackX - 无快照时使用的横坐标。
 * @returns 光标矩形；选区未折叠时返回 null。
 */
export const getCaretRect = (
    snapshot: Snapshot | null,
    range: TextSelectionRange | null,
    fallbackHeight = 1,
    fallbackX = 0,
): Rect | null => {
    if (!range || range.start !== range.end) return null;
    const fallback = { x: fallbackX, y: 0, width: 1.5, height: Math.max(fallbackHeight, 1) };
    if (!snapshot) return fallback;
    if (snapshot.lineMetrics.length === 0) {
        return snapshot.writingMode === 'vertical-rl'
            ? { x: -Math.max(fallbackHeight, 1), y: 0, width: Math.max(fallbackHeight, 1), height: 1.5 }
            : fallback;
    }

    const offset = range.end;
    const lineIndex = findLineIndexByOffset(snapshot.lineMetrics, offset);
    const line = snapshot.lineMetrics[lineIndex];
    if (!line) return null;
    const hitboxes = getHitboxesForSnapshotLine(snapshot, lineIndex);
    const inline = hitboxes.length
        ? getOffsetInline(snapshot, hitboxes, line, lineIndex, offset)
        : getLineInlineStart(snapshot, line);

    if (snapshot.writingMode === 'vertical-rl') {
        return {
            x: line.leftEdgeX,
            y: inline,
            width: Math.max(line.rightEdgeX - line.leftEdgeX, 1),
            height: 1.5,
        };
    }

    return {
        x: inline,
        y: line.top,
        width: 1.5,
        height: Math.max(line.bottom - line.top, 1),
    };
};

/**
 * 计算空文本时光标和透明 textarea 使用的兜底行高。
 *
 * @param fontSize - 当前展示投影的字号。
 * @param lineSpacing - 当前展示投影的行高倍数。
 * @returns 至少 1px 的兜底行高。
 */
export const getFallbackLineSize = (fontSize: number, lineSpacing: number) =>
    Math.max(Math.ceil(fontSize * (lineSpacing || 1.2)), 1);

/**
 * 计算空文本在不同对齐方式下的光标横坐标。
 *
 * @param textAlign - 文本对齐方式。
 * @param width - 编辑图层宽度。
 * @returns 空文本光标的横坐标。
 */
export const getEmptyCaretX = (textAlign: TextAlignType, width: number) => {
    if (textAlign === TextAlignType.CENTER) return width / 2;
    if (textAlign === TextAlignType.RIGHT) return width;
    return 0;
};

/**
 * 将目标行和行内坐标转换为 UTF-8 字节偏移。
 *
 * 垂直移动光标只需要目标行的视觉边界，因此按行读取 hitbox 缓存，不再为一次方向键操作构建全文数据。
 * 超出首尾行时仍按原有规则收敛到文档起止偏移。
 *
 * @param snapshot - 当前排版快照。
 * @param targetLineIndex - 目标行索引。
 * @param desiredInline - 当前书写方向上的目标行内坐标。
 * @returns 对应的字节偏移；无可用排版信息时返回 null。
 */
export const resolveByteOffsetAtLineInline = (
    snapshot: Snapshot | null,
    targetLineIndex: number,
    desiredInline: number,
): number | null => {
    if (!snapshot || snapshot.lineMetrics.length === 0) return null;
    if (targetLineIndex < 0) return snapshot.lineMetrics[0].byteStart;
    if (targetLineIndex >= snapshot.lineMetrics.length) {
        return snapshot.lineMetrics[snapshot.lineMetrics.length - 1].byteEnd;
    }

    const line = snapshot.lineMetrics[targetLineIndex];
    const hitboxes = getHitboxesForSnapshotLine(snapshot, targetLineIndex);
    if (hitboxes.length === 0) return line.byteStart;
    if (desiredInline <= getLineInlineStart(snapshot, line)) return getVisualEdgeOffset(hitboxes[0], 'start');
    if (desiredInline >= getLineInlineEnd(snapshot, line)) {
        return getVisualEdgeOffset(hitboxes[hitboxes.length - 1], 'end');
    }

    const hitbox = findHitboxByInline(hitboxes, desiredInline) ?? hitboxes[0];
    return resolveHitboxOffset(hitbox, desiredInline);
};

/**
 * 沿视觉顺序查找当前行的相邻光标偏移。
 *
 * 当前行的 hitboxes 会按需创建并复用；边界列表同时保留 bidi run 的视觉顺序，避免横排或竖排的
 * inline 方向移动触发全文 hitbox 构建。
 *
 * @param snapshot - 当前排版快照。
 * @param lineIndex - 当前行索引。
 * @param offset - 当前 UTF-8 字节偏移。
 * @param direction - 视觉移动方向，-1 向前，1 向后。
 * @returns 相邻的字节偏移；快照或行不存在时返回 null。
 */
export const resolveAdjacentInlineOffset = (
    snapshot: Snapshot | null,
    lineIndex: number,
    offset: number,
    direction: -1 | 1,
): number | null => {
    if (!snapshot) return null;
    const line = snapshot.lineMetrics[lineIndex];
    if (!line) return null;

    const boundaries: number[] = [];
    for (const hitbox of getHitboxesForSnapshotLine(snapshot, lineIndex)) {
        for (const edge of ['start', 'end'] as const) {
            const boundary = getVisualEdgeOffset(hitbox, edge);
            if (boundaries[boundaries.length - 1] !== boundary) boundaries.push(boundary);
        }
    }
    if (boundaries.length === 0) return direction < 0 ? line.byteStart : line.byteEnd;

    const currentIndex = boundaries.indexOf(offset);
    if (currentIndex < 0) return direction < 0 ? line.byteStart : line.byteEnd;
    return boundaries[Math.max(0, Math.min(boundaries.length - 1, currentIndex + direction))];
};

/**
 * 查找字节偏移所在的排版行。
 *
 * @param snapshot - 当前排版快照。
 * @param offset - UTF-8 字节偏移量。
 * @returns 行索引；无可用排版信息时返回 -1。
 */
export const getLineIndexFromOffset = (snapshot: Snapshot | null, offset: number) => {
    if (!snapshot || snapshot.lineMetrics.length === 0) return -1;
    return findLineIndexByOffset(snapshot.lineMetrics, offset);
};

/**
 * 计算文本选区覆盖的视觉矩形。
 *
 * @param snapshot - 当前排版快照。
 * @param range - 当前文本选区。
 * @returns 按视觉位置排序的选区矩形。
 */
export const getSelectionRects = (snapshot: Snapshot | null, range: TextSelectionRange | null): Rect[] => {
    if (!snapshot || !range || range.start === range.end) return [];
    const start = Math.min(range.start, range.end);
    const end = Math.max(range.start, range.end);
    const hitboxes = buildHitboxes(snapshot).filter((hitbox) => hitbox.byteEnd > start && hitbox.byteStart < end);
    const rects: Rect[] = [];
    const linesWithRects = new Set<number>();

    for (const group of groupByVisualRun(hitboxes)) {
        const lineIndex = group[0]?.lineIndex ?? -1;
        const minX = Math.min(...group.map((hitbox) => hitbox.x));
        const minY = Math.min(...group.map((hitbox) => hitbox.y));
        const maxX = Math.max(...group.map((hitbox) => hitbox.x + hitbox.width));
        const maxY = Math.max(...group.map((hitbox) => hitbox.y + hitbox.height));
        if (![minX, minY, maxX, maxY].every(Number.isFinite) || maxX <= minX || maxY <= minY) continue;

        rects.push({ x: minX, y: minY, width: maxX - minX, height: maxY - minY });
        linesWithRects.add(lineIndex);
    }

    snapshot.lineMetrics.forEach((line, index) => {
        if (linesWithRects.has(index) || line.byteStart < start || line.byteEnd > end) return;
        rects.push(
            snapshot.writingMode === 'vertical-rl'
                ? {
                      x: line.leftEdgeX,
                      y: line.top + snapshot.listMarkerIndent,
                      width: Math.max(line.rightEdgeX - line.leftEdgeX, 1),
                      height: 4,
                  }
                : {
                      x: line.leftEdgeX + snapshot.listMarkerIndent,
                      y: line.top,
                      width: 4,
                      height: Math.max(line.bottom - line.top, 1),
                  },
        );
    });

    return rects.sort((a, b) => a.y - b.y || a.x - b.x);
};
